import type { ReactNode } from 'react';
import type { NetworkSiteRef } from '../../api/network.js';
import { SafeLink } from '../../components/ui.js';

/**
 * 리퍼러 목록·통계 · 광고 사이트 · 계정정보 네 화면이 같이 쓰는 조각들.
 *
 * 원본(report_network_referer*.php, report_network_adinfo.php)은 네 파일이
 * 서로를 복사해 만든 것이라 머리글·검색칸·행 번호 매기기·도메인 링크가 전부
 * 같은 모양이었다 - 여기서도 한 군데에 두고 같이 쓴다.
 */

/** 원본 page_start 의 기본 한 페이지 크기 (네 화면 모두 50 이었다). */
export const PER_PAGE = 50;

/**
 * 행 번호 — 원본 `$nno = ($binfo[total] - $binfo['now_page']); ... $nno--` 다.
 * 맨 위가 전체 건수이고 아래로 내려가며 하나씩 줄어든다.
 */
export function descendingNumber(total: number, page: number, perPage: number, index: number): number {
  return total - ((page - 1) * perPage + index);
}

/** '202410' → '2024-10'. 저장값은 붙여 쓴 여섯 자리다. */
export function monthLabel(yearmonth: string): string {
  return /^\d{6}$/.test(yearmonth) ? `${yearmonth.slice(0, 4)}-${yearmonth.slice(4)}` : yearmonth;
}

/** 그 달의 1일과 말일 ('YYYY-MM-DD'). 통계 화면의 '상세검색' 링크가 쓴다. */
export function monthBounds(yearmonth: string): { from: string; to: string } {
  const year = Number(yearmonth.slice(0, 4));
  const month = Number(yearmonth.slice(4));
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${last}` };
}

/** 리퍼러(사이트) 타입 — 원본 network_site_add.php 의 hosttype 라디오. */
export const HOST_TYPE_LABELS: Readonly<Record<string, string>> = {
  adsite: '광고 사이트',
  adserver: '광고 서버',
  adult: '성인 영상',
  blog: '성인 블로그',
  comic: '성인 만화',
  etc: '기타',
};

/** 광고 가능 타입 — 원본 bannertype(사이트) · ad_type(계정정보). */
export const AD_TYPE_LABELS: Readonly<Record<string, string>> = {
  popunder: '팝언더',
  banner: '배너',
  etc: '기타',
};

/** 결제 방식 — 원본 network_adinfo_add.php 의 pay_type. */
export const PAY_TYPE_LABELS: Readonly<Record<string, string>> = {
  paypal: '페이팔',
  card: '신용카드',
  etc: '기타',
};

/** 계정 진행 상태 — 원본 report_network_adinfo.php:146-154 의 switch. */
export const AD_ACCOUNT_STATE_LABELS: Readonly<Record<string, string>> = {
  wait: '진행대기',
  ing: '진행중',
  stop: '정지',
  not: '불가',
  waiting: '추후재시도',
};

/**
 * 원본이 상태마다 쓰던 색을 이 디자인 시스템의 뱃지 색으로 옮긴 것이다
 * (진행대기 green, 진행중 white, 정지 red, 불가 pink, 추후재시도 blue).
 */
export const AD_ACCOUNT_STATE_TONE: Readonly<Record<string, string>> = {
  wait: 'ok',
  ing: 'info',
  stop: 'danger',
  not: 'warn',
  waiting: 'sky',
};

/** 표에 없는 코드는 원본 switch 의 default 처럼 코드값을 그대로 보여준다. */
export function labelOf(labels: Readonly<Record<string, string>>, code: string | null | undefined): string {
  if (!code) return '-';
  return labels[code] ?? code;
}

/**
 * 접속기기 - 저장값은 'p'/'m' 이다. 원본 표는 이 글자를 그대로 찍었는데,
 * 뜻을 아는 사람만 읽을 수 있어서 글자로 바꾸고 원래 코드는 title 에 남긴다.
 */
export function AgentLabel({ agent }: { agent: string | null }) {
  if (!agent) return <span className="td-muted">-</span>;
  const key = agent.toLowerCase();
  const label = key === 'p' ? 'PC' : key === 'm' ? '모바일' : agent;
  return <span title={agent}>{label}</span>;
}

/**
 * 리퍼러 도메인 링크 — 원본은 `window.open('http://' + refhost)` 였다(:204).
 * 저장값이 도메인뿐이라 http:// 를 붙여 주고, 주소로 볼 수 없는 값이면
 * SafeLink 가 링크를 만들지 않고 글자만 남긴다.
 */
export function HostLink({ host }: { host: string | null }) {
  if (!host) return <span className="td-muted">-</span>;
  return (
    <SafeLink href={`http://${host}`} className="link">
      {host}
    </SafeLink>
  );
}

/**
 * 그 도메인이 '광고 사이트'에 등록돼 있는지 - 원본은 등록돼 있으면 사이트
 * 타입을 검은 뱃지로, 없으면 노란 '추가' 버튼(등록 팝업)을 보여줬다(:205-209).
 * 등록 팝업은 이번 이관 범위 밖이라, 여기서는 등록 여부만 알려준다.
 */
export function SiteBadge({ site }: { site: NetworkSiteRef | null }) {
  // 값이 없을 때 뱃지를 그리지 않고 흐린 글자만 두는 건 Cell.tsx 의 badge 칸과
  // 같은 규칙이다 (이 디자인 시스템에 badge--muted 는 없다).
  if (!site) return <span className="td-muted">미등록</span>;
  return <span className="badge">{labelOf(HOST_TYPE_LABELS, site.hosttype)}</span>;
}

/** '|' 로 이어 붙여 저장하던 여러 값을 뱃지로 편다. */
export function CodeBadges({
  codes,
  labels,
}: {
  codes: string[];
  labels: Readonly<Record<string, string>>;
}) {
  if (codes.length === 0) return <span className="td-muted">-</span>;
  return (
    <span className="network-badges">
      {codes.map((code) => (
        <span className="badge" key={code}>
          {labelOf(labels, code)}
        </span>
      ))}
    </span>
  );
}

export interface DetailField {
  label: string;
  value: ReactNode;
  /** 긴 원문(유입 URL·메모 등)은 줄바꿈을 살려 그대로 보여준다. */
  pre?: boolean;
}

/**
 * 상세 보기.
 *
 * 원본은 긴 값(유입 URL·파라미터·메모·파트너 코드들)을 표 안 textarea 에 담아
 * 그 칸에서 바로 긁어 복사하게 했다. 표를 좁게 유지하면서 같은 일을 할 수
 * 있도록, 표에서는 줄여 보여주고 전체는 이 상세 보기에서 보여준다.
 */
export function DetailModal({
  title,
  fields,
  onClose,
}: {
  title: string;
  fields: DetailField[];
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{title}</h2>
        <dl className="network-detail">
          {fields.map((f) => (
            <div className="network-detail__row" key={f.label}>
              <dt>{f.label}</dt>
              <dd className={f.pre ? 'network-detail__pre' : undefined}>{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

/**
 * 조회할 달. 선택지는 자료가 있는 달만 서버에서 온다 - 지금 고른 달이 거기
 * 없으면(예: 자료가 끊긴 뒤의 이번 달) 맨 앞에 끼워 넣어 무엇을 보고 있는지
 * 드러나게 한다.
 */
export function MonthSelect({
  months,
  value,
  onChange,
}: {
  months: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const options = value && !months.includes(value) ? [value, ...months] : months;
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.length === 0 && <option value={value}>{monthLabel(value)}</option>}
      {options.map((m) => (
        <option key={m} value={m}>
          {monthLabel(m)}
        </option>
      ))}
    </select>
  );
}
